import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./css/Toast.module.css";

interface ToastProps {
  open: boolean;
  message: ReactNode;
  onClose: () => void;
  icon?: ReactNode;
  duration?: number;
}

export default function Toast({
  open,
  message,
  onClose,
  icon,
  duration = 3000,
}: ToastProps) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // 닫히는 애니메이션 동안 메시지가 비지 않도록 마지막 메시지를 유지한다
  const lastMessageRef = useRef(message);
  if (message) lastMessageRef.current = message;

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => onCloseRef.current(), duration);
    return () => window.clearTimeout(timer);
  }, [open, message, duration]);

  return createPortal(
    <div className={styles.region} role="status" aria-live="polite">
      <AnimatePresence>
        {open && (
          <motion.div
            className={styles.toast}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: [0.2, 0, 0, 1] }}
          >
            {icon && <span className={styles.icon}>{icon}</span>}
            <span className={styles.message}>{lastMessageRef.current}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body
  );
}
